import AuthService from './authService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8007';
const CHAT_ENDPOINT = `${API_BASE}/api/v1/chat/`;

/**
 * Send a message and stream the response via SSE.
 *
 * @param {string} message - The user's message.
 * @param {Array<{role: string, content: string}>} history - Prior conversation turns.
 * @param {{ onToken: (text: string) => void, onIntent: (intent: string) => void, onToolStart: (tool: string) => void, onToolEnd: () => void, onDone: () => void, onError: (err: Error) => void }} callbacks
 * @returns {() => void} Abort function — call it to cancel the stream.
 */
export function streamChat(message, history, { onToken, onIntent, onToolStart, onToolEnd, onDone, onError }) {
  const controller = new AbortController();

  const token = AuthService.getToken();

  fetch(CHAT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, history }),
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Chat request failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function pump() {
        reader.read().then(({ done, value }) => {
          if (done) {
            onDone();
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete last line in buffer

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            try {
              const event = JSON.parse(raw);
              if (event.type === 'token' && onToken) onToken(event.content);
              if (event.type === 'intent' && onIntent) onIntent(event.intent);
              if (event.type === 'tool_start' && onToolStart) onToolStart(event.tool);
              if (event.type === 'tool_end' && onToolEnd) onToolEnd();
              if (event.type === 'error' && onToken) onToken(event.content);
              if (event.type === 'done') { onDone(); return; }
            } catch {
              // ignore malformed SSE lines
            }
          }

          pump();
        });
      }

      pump();
    })
    .catch((err) => {
      if (err.name !== 'AbortError') onError(err);
    });

  return () => controller.abort();
}

const ANALYTICS_ENDPOINT = `${API_BASE}/api/v1/chat/analytics`;

/**
 * Fetch AI chat analytics for the given number of days.
 * @param {number} days
 * @returns {Promise<object>}
 */
export async function getChatAnalytics(days = 30) {
  const token = AuthService.getToken();
  const res = await fetch(`${ANALYTICS_ENDPOINT}?days=${days}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Analytics request failed: ${res.status}`);
  return res.json();
}
